import { zodResolver } from '@hookform/resolvers/zod';
import { SettingUrls } from 'constants/SettingsUrls';
import { SettingKey, WizardStepName, type PackageProviderName } from 'data/ppoints/constants';
import RateForProviderSelector from 'features/shipping-provider/rate-for-provider-selector/RateForProviderSelector';
import RateForProviderSelectorSkeleton from 'features/shipping-provider/rate-for-provider-selector/RateForProviderSelectorSkeleton';
import { useShippingProviderCountries } from 'features/shipping-provider/hooks/useShippingProviderCountries';
import WizardStepItemDone from 'features/wizard/components/WizardStepItemDone';
import useCreateShippingZone from 'features/wizard/hooks/useCreateShippingZone';
import useEnsureDefaultIntegrationMode from 'features/wizard/hooks/useEnsureDefaultIntegrationMode';
import useReportStepLoading from 'features/wizard/hooks/useReportStepLoading';
import useSaveZoneRates, { getZoneFieldKey } from 'features/wizard/hooks/useSaveZoneRates';
import useWizard from 'features/wizard/hooks/useWizard';
import useZonesWithMappedProviders from 'features/wizard/hooks/useZonesWithMappedProviders';
import { WizardStepContentProps } from 'features/wizard/models/WizardStepContentProps';
import { useGetCountryName } from 'hooks/ppoints/useGetCountryName';
import useContextualSaveBar from 'hooks/useContextualSaveBar';
import { useCountries } from 'hooks/useCountries';
import useSettings from 'hooks/useSettings';
import { capitalize } from 'lodash-es';
import {
    ShippingMethodsSetting,
    ShippingMethodsSettingProviders,
} from 'models/ppoints/settings/ShippingMethodsSetting';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { CommonMapper } from 'utils/mapper/common.mapper';
import { ShippingZoneRateStepForm, ZoneProviderRates } from '../../models/shipping-zone-rate-step-form';
import shippingZoneRateStepSchema from './schema/shippingZoneRateStepSchema';

const SAVE_BAR_ID = 'wizard-shipping-zone-rate-savebar';

const EMPTY_RATES: ZoneProviderRates = {};

const ProviderList: React.FC<{ providers: PackageProviderName[] }> = ({ providers }) => (
    <div className="flex flex-row flex-wrap gap-2">
        {providers.map((provider) => (
            <s-badge key={provider} tone="neutral">
                {PackageProviderService.getDisplayName(provider)}
            </s-badge>
        ))}
    </div>
);

export const ShippingZoneRateStep: React.FC<WizardStepContentProps> = ({ onLoadingChange }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const wizard = useWizard();
    const isDone = wizard.isStepDone(WizardStepName.shipping_zone_rate);
    const { zones, countriesWithoutZone, isLoading: isLoadingZones, refetchZones } = useZonesWithMappedProviders();
    const { getTranslatedCountryName } = useGetCountryName();
    const { data: countries } = useCountries();
    const { settings: shippingMethodsSetting, fetching: isLoadingShippingMethods } =
        useSettings<ShippingMethodsSetting>(SettingKey.shipping_methods);
    const { providerToCountry, isLoading: isLoadingProviderCountries } = useShippingProviderCountries();

    // Gated together so defaultZoneRates below is computed with the saved rates already in hand —
    // otherwise the form's initial reset could fire before shippingMethodsSetting/providerToCountry
    // finish loading.
    const isLoading = isLoadingZones || isLoadingShippingMethods || isLoadingProviderCountries;

    // Flat set of every provider actually assigned to at least one country in provider_to_country —
    // same "active provider" notion ShippingProviderCard.tsx uses, sourced from the same setting
    // useShippingProviderCountries already reads, rather than a separate per-provider status check.
    const activeProviderNames = useMemo(
        () => new Set(Object.values(providerToCountry ?? {}).flat()),
        [providerToCountry]
    );

    // A provider can be assigned to a zone's country (provider_to_country) at the time this zone was
    // matched, yet no longer be active — drop those before they reach the rate selector, default
    // values, or the save call.
    const activeZones = useMemo(
        () =>
            zones
                .map((zone) => ({
                    ...zone,
                    providers: zone.providers.filter((provider) => activeProviderNames.has(provider)),
                }))
                .filter((zone) => zone.providers.length > 0),
        [zones, activeProviderNames]
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditingRates, setIsEditingRates] = useState(false);

    const handleRatesUpdated = useCallback(() => refetchZones(), [refetchZones]);

    const { createZone, isCreatingZone, creatingCountry } = useCreateShippingZone(handleRatesUpdated);

    useReportStepLoading(isLoading || isSubmitting || isEditingRates || isCreatingZone, onLoadingChange);

    const tKey = i18n.t.bind(i18n) as (key: string) => string;

    const form = useForm<ShippingZoneRateStepForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(shippingZoneRateStepSchema),
        defaultValues: {
            zones: {},
        },
    });

    // Falls back to a fresh {} while the setting hasn't loaded yet or nothing's been saved.
    const savedProviderRates: ShippingMethodsSettingProviders = useMemo(
        () =>
            (shippingMethodsSetting != null &&
                (shippingMethodsSetting['providers'] as ShippingMethodsSettingProviders)) ||
            {},
        [shippingMethodsSetting]
    );

    const defaultZoneRates = useMemo(
        () =>
            activeZones.reduce<ShippingZoneRateStepForm['zones']>((defaults, zone) => {
                const zoneKey = getZoneFieldKey(zone.id);
                defaults[zoneKey] = zone.providers.reduce<ZoneProviderRates>((providers, provider) => {
                    const savedRates = savedProviderRates[`provider_${provider}`]?.[zoneKey] ?? [];
                    // The settings round-trip can hand `id` back as a string even though ShippingRate
                    // types it as number — every id-based match downstream (rateOwners, the labelCache
                    // in usePageableOptions) uses strict equality, so an un-coerced string id silently
                    // fails to resolve a name and falls back to displaying the raw value.
                    providers[provider] = savedRates.map((rate) =>
                        CommonMapper.mapStringIntoNumber(rate.id as unknown as string)
                    );
                    return providers;
                }, {});
                return defaults;
            }, {}),
        [activeZones, savedProviderRates]
    );

    const zonesStructure = useMemo(
        () => activeZones.map((zone) => `${zone.id}:${zone.providers.join(',')}`).join('|'),
        [activeZones]
    );

    const hasSelectableRates = useMemo(
        () =>
            activeZones.some(
                (zone) =>
                    zone.zone.price_based_shipping_rates.length > 0 || zone.zone.weight_based_shipping_rates.length > 0
            ),
        [activeZones]
    );

    const defaultZoneRatesRef = useRef(defaultZoneRates);
    defaultZoneRatesRef.current = defaultZoneRates;

    useEffect(() => {
        form.reset({ zones: defaultZoneRatesRef.current });
    }, [zonesStructure, form.reset]);

    const { saveZoneRates } = useSaveZoneRates();
    const { ensureDefaultIntegrationMode } = useEnsureDefaultIntegrationMode();

    const onSubmit = useCallback(
        async (data: ShippingZoneRateStepForm) => {
            const previousDefaults = form.formState.defaultValues as ShippingZoneRateStepForm;

            setIsSubmitting(true);

            if (!form.formState.isDirty) {
                await ensureDefaultIntegrationMode();
                await wizard.handleWizardStepper(WizardStepName.shipping_zone_rate);
                setIsSubmitting(false);
                return;
            }

            form.reset(data);

            let saved: boolean;
            try {
                saved = await saveZoneRates(activeZones, data);
            } finally {
                setIsSubmitting(false);
            }

            if (!saved) {
                form.reset(previousDefaults);
                form.reset(data, { keepDefaultValues: true });
            }
        },
        [form, saveZoneRates, activeZones, ensureDefaultIntegrationMode, wizard]
    );

    const { saveBar } = useContextualSaveBar<ShippingZoneRateStepForm>(form, onSubmit, SAVE_BAR_ID, false);

    useLayoutEffect(() => {
        return () => {
            shopify.saveBar.hide(SAVE_BAR_ID).catch(() => {});
        };
    }, []);

    const getCountryLabel = useCallback(
        (name: string) => {
            const code = countries?.find((country) => country.name === name)?.country_code;
            return code ? getTranslatedCountryName(code) : capitalize(name);
        },
        [countries, getTranslatedCountryName]
    );

    if (isDone) {
        return (
            <WizardStepItemDone
                message={t('WIZARD.SHIPPING_ZONE_RATE_STEP.DESCRIPTION')}
                action={
                    <s-button onClick={() => navigate(SettingUrls.shippingZones)}>
                        {t('WIZARD.SHIPPING_ZONE_RATE_STEP.MODIFY_SETTINGS')}
                    </s-button>
                }
            />
        );
    }

    if (isLoading) {
        return (
            <div
                className="flex flex-col gap-2"
                aria-busy="true"
                aria-label={t('WIZARD.SHIPPING_ZONE_RATE_STEP.TITLE')}
            >
                <s-text>{t('WIZARD.SHIPPING_ZONE_RATE_STEP.INTRO')}</s-text>
                <RateForProviderSelectorSkeleton />
            </div>
        );
    }

    const zonesError = form.formState.errors.zones as unknown as FieldError & { root?: FieldError };
    const rateError = zonesError?.message ?? zonesError?.root?.message;

    return (
        <div className="flex flex-col gap-2">
            {saveBar}
            <s-text>{t('WIZARD.SHIPPING_ZONE_RATE_STEP.INTRO')}</s-text>

            {activeZones.length === 0 && countriesWithoutZone.length === 0 && (
                <div className="pt-2">
                    <s-text color="subdued">{t('WIZARD.SHIPPING_ZONE_RATE_STEP.EMPTY')}</s-text>
                </div>
            )}

            {activeZones.map((zone) => (
                <Controller
                    key={zone.id}
                    control={form.control}
                    name={`zones.${getZoneFieldKey(zone.id)}`}
                    render={({ field, fieldState }) => (
                        <div className="flex flex-col gap-1">
                            <RateForProviderSelector
                                zone={zone}
                                ratesByProvider={field.value ?? EMPTY_RATES}
                                onRatesChange={field.onChange}
                                onRatesUpdated={handleRatesUpdated}
                                onLoadingChange={setIsEditingRates}
                            />
                            {fieldState.error?.message && (
                                <s-text tone="critical">{tKey(fieldState.error.message)}</s-text>
                            )}
                        </div>
                    )}
                />
            ))}

            {rateError && <s-text tone="critical">{tKey(rateError)}</s-text>}

            {countriesWithoutZone.length > 0 && (
                <div className="pt-2">
                    <s-banner tone="warning" heading={t('WIZARD.SHIPPING_ZONE_RATE_STEP.NO_ZONE_WARNING.HEADING')}>
                        <s-text>{t('WIZARD.SHIPPING_ZONE_RATE_STEP.NO_ZONE_WARNING.BODY')}</s-text>
                        <div className="grid gap-2 pt-2">
                            {countriesWithoutZone.map((country) => (
                                <div className="grid gap-1" key={country.name}>
                                    <s-text>{getCountryLabel(country.name)}</s-text>
                                    <div className="flex flex-row">
                                        <s-button
                                            onClick={() => createZone(country.rawName)}
                                            loading={isCreatingZone && creatingCountry === country.rawName}
                                            disabled={isCreatingZone}
                                        >
                                            {t('BUTTON.CREATE_ZONE')}
                                        </s-button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </s-banner>
                </div>
            )}
            {countriesWithoutZone.length === 0 && activeZones.length > 0 && hasSelectableRates && (
                <div className="flex justify-end pt-1">
                    <s-button
                        variant="primary"
                        loading={isSubmitting}
                        onClick={() => {
                            form.handleSubmit(onSubmit)();
                        }}
                    >
                        {t('WIZARD.STEP_NEXT_BUTTON')}
                    </s-button>
                </div>
            )}
        </div>
    );
};
